import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { GLOSSARY, groupByCategory, type GlossaryEntry, type GlossaryRegime } from "../lib/glossary";

interface Props {
  open: boolean;
  onClose: () => void;
}

const REGIME_FILTERS: Array<{ id: "all" | GlossaryRegime; label: string }> = [
  { id: "all", label: "All" },
  { id: "UK", label: "UK" },
  { id: "US", label: "US" },
  { id: "Both", label: "Both" },
];

export default function Glossary({ open, onClose }: Props) {
  const [filter, setFilter] = useState<"all" | GlossaryRegime>("all");
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = GLOSSARY.filter((e) => {
      if (filter !== "all" && e.regime !== filter) return false;
      if (!q) return true;
      const hay = `${e.term} ${e.short} ${e.detail} ${e.usAnalog ?? ""}`.toLowerCase();
      return hay.includes(q);
    });
    const map = new Map<string, GlossaryEntry[]>();
    for (const e of filtered) {
      const list = map.get(e.category) ?? [];
      list.push(e);
      map.set(e.category, list);
    }
    // Preserve original group order from groupByCategory
    const ordered = new Map<string, GlossaryEntry[]>();
    for (const cat of groupByCategory().keys()) {
      if (map.has(cat)) ordered.set(cat, map.get(cat)!);
    }
    return ordered;
  }, [filter, query]);

  if (!open) return null;

  const totalShown = Array.from(grouped.values()).reduce((acc, l) => acc + l.length, 0);

  return createPortal(
    <div
      className="glossary-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label="Construction finance glossary"
      onClick={onClose}
    >
      <div className="glossary-modal" onClick={(e) => e.stopPropagation()}>
        <div className="glossary-head">
          <div>
            <div className="glossary-eyebrow">Reference</div>
            <h2 className="glossary-title">Construction finance glossary</h2>
            <div className="glossary-sub">
              UK · US · cross-border terminology. Useful when reading UK contractor reports or
              when this dashboard is extended to a UK / cross-border audience.
            </div>
          </div>
          <button
            type="button"
            className="glossary-close"
            aria-label="Close glossary"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="glossary-toolbar">
          <div className="glossary-filter">
            {REGIME_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                className={`glossary-pill${filter === f.id ? " active" : ""}`}
                onClick={() => setFilter(f.id)}
              >
                {f.label}
              </button>
            ))}
          </div>
          <input
            type="search"
            className="glossary-search"
            placeholder="Search terms…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>

        <div className="glossary-body">
          {totalShown === 0 ? (
            <div className="glossary-empty">No terms match the current filter.</div>
          ) : (
            Array.from(grouped.entries()).map(([cat, entries]) => (
              <section key={cat} className="glossary-section">
                <h3 className="glossary-section-title">{cat}</h3>
                <ul className="glossary-list">
                  {entries.map((e) => (
                    <li key={e.term} className="glossary-card">
                      <div className="glossary-card-head">
                        <span className="glossary-term">{e.term}</span>
                        <span className={`glossary-regime regime-${e.regime.toLowerCase()}`}>
                          {e.regime}
                        </span>
                      </div>
                      <div className="glossary-short">{e.short}</div>
                      <div className="glossary-detail">{e.detail}</div>
                      {e.usAnalog && (
                        <div className="glossary-analog">
                          <span className="glossary-label">US analog</span> {e.usAnalog}
                        </div>
                      )}
                      {e.seeAlso && e.seeAlso.length > 0 && (
                        <div className="glossary-seealso">
                          <span className="glossary-label">See also</span>{" "}
                          {e.seeAlso.join(" · ")}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>

        <div className="glossary-foot">
          <span className="muted">
            {totalShown} of {GLOSSARY.length} terms shown
          </span>
          <span className="muted">Esc to close</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
